import "antd/dist/antd.css";
import "../App.css";

import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import GuideArticle from "../components/GuideArticle";
import GuideSection from "../components/GuideSection";
import GuideTripLead from "../components/GuideTripLead";
import GuideDayCarousel from "../components/GuideDayCarousel";
import GuideCtaBand from "../components/GuideCtaBand";
import RelatedGuides from "../components/RelatedGuides";
import { wrapBookingWithCj } from "../cjLink";
import usePageMeta from "../usePageMeta";
import { GUIDES } from "../data/guides";

const META = GUIDES.find((g) => g.slug === "grand-tour-europe")!;

const TRIP_STATS = [
    { value: "12", label: "days" },
    { value: "4", label: "bikes" },
    { value: "5", label: "countries" },
    { value: "~2,000", label: "miles" },
    { value: "5", label: "passes" },
];

const ROUTE_FLAGS = [
    { emoji: "🇬🇧", label: "UK" },
    { emoji: "🇫🇷", label: "France" },
    { emoji: "🇨🇭", label: "Switzerland" },
    { emoji: "🇮🇹", label: "Italy" },
    { emoji: "🇩🇪", label: "Germany" },
    { emoji: "🇧🇪", label: "Belgium" },
    { emoji: "🇬🇧", label: "UK" },
];

// Day-rail accents, pulled from the brand --gt-feature-* palette per the
// design handoff's country/phase colour mapping.
const DAY_ACCENT = {
    teal: { fill: "#0f4c5c", chipBg: "rgba(15,76,92,.12)", chipText: "#0f4c5c" },
    green: { fill: "#5b8a72", chipBg: "rgba(91,138,114,.14)", chipText: "#3f6b53" },
    rust: { fill: "#c1654a", chipBg: "rgba(193,101,74,.13)", chipText: "#a1462c" },
    slate: { fill: "#5a6772", chipBg: "rgba(90,103,114,.13)", chipText: "#485460" },
    plum: { fill: "#7a4f6d", chipBg: "rgba(122,79,109,.14)", chipText: "#5f3c54" },
} as const;

type DayPhoto = { src: string; alt: string; caption?: string };
type DayHotel = {
    name: string;
    bookingUrl: string;
    bookingSid: string;
    tripadvisorUrl: string;
    body: ReactNode;
};
type Day = {
    flag: string;
    place: string;
    title: string;
    summary: string;
    accent: keyof typeof DAY_ACCENT;
    narrative: ReactNode;
    photos?: DayPhoto[];
    hotel?: DayHotel;
};

const DAYS: Day[] = [
    {
        flag: "🇫🇷",
        place: "La Chaussée-sur-Marne",
        title: "Under the Channel",
        summary:
            "Le Shuttle from Folkestone, then a run into the flat farmland of northern France.",
        accent: "teal",
        narrative: (
            <>
                <p>
                    We rolled into the Folkestone terminal early, helmets
                    steaming in the morning cool, and met Toby at the
                    check-in. There's no romance to Le Shuttle — you ride
                    your bike into a metal box, the doors close, and
                    thirty-five minutes later you're in another country. But
                    there's something undeniably good about watching England
                    disappear into a tunnel mouth and emerging into the
                    wide, flat farmland of northern France.
                </p>
                <p>
                    We pushed on to Le Clos de Mutigny, a quiet spot near
                    La Chaussée-sur-Marne with private parking for the
                    bikes, dropped the panniers, and went hunting for
                    dinner and the first proper bottle of wine of the
                    trip. The Alps were still a long way south. That was
                    tomorrow's problem.
                </p>
            </>
        ),
        photos: [
            {
                src: "/assets/grand_tour_2026/d1_hotel.jpg",
                alt: "Le Clos de Mutigny seen from the front: a cream-painted bourgeois residence with Hôtel Restaurant lettering above the windows, set behind a hedge and railings under a grey sky",
                caption: "Le Clos de Mutigny",
            },
            {
                src: "/assets/grand_tour_2026/d1-parked.jpg",
                alt: "Four motorbikes parked for the night at Le Clos de Mutigny — three under silver-and-black covers and a red Ducati Multistrada uncovered, tall trees and an ornate lamp post in the background",
                caption: "Bikes parked for the night",
            },
        ],
        hotel: {
            name: "Le Clos de Mutigny, La Chaussée-sur-Marne",
            bookingUrl: "https://www.booking.com/hotel/fr/closdemutigny.html",
            bookingSid: "grand_tour_2026_day1",
            tripadvisorUrl:
                "https://www.tripadvisor.com/Hotel_Review-g1079986-d280075-Reviews-Le_Clos_de_Mutigny-La_Chaussee_sur_Marne_Marne_Grand_Est.html",
            body: (
                <>
                    A quiet hotel-restaurant in a wooded park near the Marne
                    — beautiful food, an honest wine list, and bike-friendly
                    hosts. Direct site:{" "}
                    <a
                        href="https://www.clos-de-mutigny.com/"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        clos-de-mutigny.com
                    </a>
                    .
                </>
            ),
        },
    },
    {
        flag: "🇨🇭",
        place: "Fiesch",
        title: "The long run south",
        summary: "The grind day. Departmental roads, then the autoroute got its turn.",
        accent: "teal",
        narrative: (
            <>
                <p>
                    This was the grind day, and every good Alpine tour has
                    one. We'd planned to do it the romantic way — cross-
                    country, off the tolls, watching France unspool one
                    departmental road at a time. Beautiful for the first
                    few hours. Then we looked at the clock at 3pm, looked
                    at the map, and realised we still had 200 km to go to
                    get to Fiesch.
                </p>
                <p>
                    So the autoroute got its turn. A few fuel and food
                    stops, the land started to fold without us noticing,
                    and we crossed into Switzerland in the long evening
                    light. The final climb up to the chalet had us
                    negotiating a handful of tight switchbacks — a little
                    tricky on tired arms after a long day, but good
                    practice for the passes still to come. We rolled up
                    at 8pm — later than we'd have liked, but luckily the
                    Fiesch Coop stays open late on Saturdays. Milk, beer,
                    breakfast essentials. Bikes parked up on the flat
                    slabs at the chalet, out of the way and secure.
                    Boots off. From the balcony you could see the
                    mountains we'd come all this way to ride. Worth every
                    motorway mile, even the late ones.
                </p>
            </>
        ),
        photos: [
            {
                src: "/assets/grand_tour_2026/d2_bikes.jpg",
                alt: "Two motorbikes parked at Chalet Arnica in Fiesch — a blue BMW GS adventure and a red Ducati Multistrada, snowcapped Alpine peaks rising behind them under a bright blue sky",
                caption: "Bikes parked at the chalet, Fiesch",
            },
            {
                src: "/assets/grand_tour_2026/d2_view.jpg",
                alt: "View from the Chalet Arnica balcony across Fiesch — neighbouring chalets in the foreground, a forested green peak rising in the middle distance, and snowcapped Alpine summits to the right under a clear blue sky",
                caption: "The view from the chalet balcony",
            },
        ],
        hotel: {
            name: "Chalet Arnica, Fiesch",
            bookingUrl: "https://www.booking.com/hotel/ch/chalet-arnica.html",
            bookingSid: "grand_tour_2026_day2",
            tripadvisorUrl:
                "https://www.tripadvisor.com/VacationRentalReview-g670028-d23792360-Chalet_Arnica-Fiesch_in_Valais_Canton_of_Valais_Swiss_Alps.html",
            body: (
                <>
                    A three-bedroom self-catering chalet on a quiet side
                    street in Fiesch, with a balcony looking out across the
                    Upper Valais Alps, a garden behind the house, and free
                    private parking for the bikes. Ten minutes' walk to the
                    Fiesch cable car for the Aletsch glacier day.
                </>
            ),
        },
    },
    {
        flag: "🇨🇭",
        place: "Fiesch · rest day",
        title: "Feet up",
        summary: "Smart touring isn't all throttle. A day to do precisely nothing.",
        accent: "green",
        narrative: (
            <p>
                Smart touring isn't all throttle. We took a day to do
                precisely nothing — feet up on the chalet balcony, a
                wander down to the local Coop to stock the kitchen, and
                an ice cream in the sun because why not. The small
                administrative joys of doing nothing in particular in a
                beautiful place. The legs were rested. The bikes were
                checked. Tomorrow we'd earn the trip.
            </p>
        ),
    },
    {
        flag: "🇨🇭",
        place: "Furka · Susten · Grimsel",
        title: "The holy trinity",
        summary: "Three of the most famous passes in Europe, strung into one perfect loop.",
        accent: "rust",
        narrative: (
            <>
                <p>If you ride motorcycles, you already know these names.</p>
                <p>
                    We strung together three of the most famous passes in
                    Europe in a single, perfect loop. <strong>Furka</strong>{" "}
                    came first — the showpiece at 2,429 metres, all
                    hairpins and exposure and that giddy sense of riding
                    along the spine of the world. (Yes, <em>that</em>{" "}
                    Furka — the one James Bond raced through in
                    Goldfinger. It hasn't lost any of its drama.) Then{" "}
                    <strong>Susten</strong>, more flowing than its
                    neighbours, fast and rhythmic, the kind of road where
                    you stop thinking and just <em>ride</em>.
                </p>
                <p>
                    And then <strong>Grimsel</strong> to close the loop —
                    2,106 metres of glassy tarmac threading past glacial
                    lakes the colour of turquoise glass, with pullouts
                    every few corners practically begging you to stop and
                    stare. Four bikes, endless switchbacks, brakes warm
                    and grins wide. This was the day the whole trip had
                    been built around, and it delivered every bit of it.
                </p>
            </>
        ),
        photos: [
            {
                src: "/assets/grand_tour_2026/d4_furka_hotel.jpg",
                alt: "The historic Hotel Belvédère on the Furka Pass — a tall yellow-and-cream hotel building perched on a hairpin bend, snow-capped Alpine peaks rising behind under a moody grey sky, motorbikes and cars parked in the foreground",
                caption: "Hotel Belvédère, Furka Pass",
            },
            {
                src: "/assets/grand_tour_2026/d4_furkapass.jpg",
                alt: "Looking down the Furka Pass road as it snakes through a green Alpine valley between two rocky ridges, patches of snow on the upper slopes and clouds drifting across a bright blue sky",
                caption: "The Furka Pass road dropping into the valley",
            },
        ],
    },
    {
        flag: "🇨🇭",
        place: "Aletsch · rest day",
        title: "Cable car to the glacier",
        summary: "A day off the bikes, up the gondola to the Aletsch.",
        accent: "green",
        narrative: (
            <p>
                A day off the bikes. We took the cable car up from
                Fiesch to look out over the glacier — that vast, ancient
                river of ice that fills the valley above the Aletsch
                region, one of the great sights of the Alps. There's
                something humbling about trading the noise of four
                engines for the silence up there, the air thin and
                clean, the ice stretching off further than you can quite
                take in. Worth every franc of the gondola ticket.
            </p>
        ),
        photos: [
            {
                src: "/assets/grand_tour_2026/d5_glacier.jpg",
                alt: "The Aletsch Glacier seen from a rocky viewpoint above Fiesch — a vast curving river of grey-white ice running through a deep valley between dark Alpine ridges, snow-capped peaks at the head of the glacier under a moody grey sky",
                caption: "Aletsch Glacier from the Fiesch viewpoint",
            },
            {
                src: "/assets/grand_tour_2026/d5_grandtour.jpg",
                alt: "A rider posing in the red shield-shaped 'Grand Tour of Switzerland' photo frame at the Aletsch viewpoint, with the glacier and dark Alpine peaks framed behind under an overcast sky",
                caption: "Grand Tour of Switzerland marker, Aletsch viewpoint",
            },
        ],
    },
    {
        flag: "🇮🇹",
        place: "Lago Maggiore",
        title: "Italy via Nufenen and Simplon",
        summary: "Over the highest all-Swiss pass, down to the lakes, back over Simplon.",
        accent: "rust",
        narrative: (
            <>
                <p>
                    Back up high for the <strong>Nufenenpass</strong>, the
                    highest paved pass that sits entirely within Switzerland,
                    and one of the quieter greats — wild, windswept and
                    gloriously empty compared to its famous neighbours.
                </p>
                <p>
                    Then we tipped over into Italy and chased the day
                    through Airolo, down to <strong>Ascona</strong> and{" "}
                    <strong>Cannobio</strong> on the shores of Lago
                    Maggiore. Lunch was one of many lakeside restaurants
                    — proper Italian food in the sun, the lake glinting
                    behind the table. When we walked back to the bikes
                    the afternoon heat had done its trick: mine had sunk
                    a few inches further into the tarmac. Helmets back
                    on. Time to chase some cooler air in the mountains.
                </p>
                <p>
                    Inland onto a stretch of properly twisty Italian
                    back-roads — the kind where you stop counting
                    corners and just settle in. One of us put a bike
                    down on one of them. Small mercies: a scratch and a
                    snapped clutch lever, still rideable, no one hurt.
                </p>
                <p>
                    We'd planned to push up the Formazza valley to the
                    waterfall, but the day was getting away from us. We
                    struck the waterfall off the list, pointed the bikes
                    back over the <strong>Simplon Pass</strong> to the
                    chalet, and called it a well-earned rest. The
                    waterfall would have to wait for tomorrow.
                </p>
            </>
        ),
        photos: [
            {
                src: "/assets/grand_tour_2026/d6_lago.jpg",
                alt: "Lakeside promenade on Lago Maggiore — a row of flags including the Belgian tricolour, the lake and forested mountainsides to the left, Italian buildings to the right under a bright blue sky",
                caption: "Lakeside promenade, Lago Maggiore",
            },
        ],
    },
    {
        flag: "🇨🇭",
        place: "Nufenenpass",
        title: "Back up Nufenen for the strudel",
        summary: `The "if it was good, ride it again" principle.`,
        accent: "teal",
        narrative: (
            <p>
                Day 7 was the "if it was good, ride it again"
                principle. Two options on the table: chase down the
                waterfall in the Formazza valley, or run the
                Nufenenpass a second time. The waterfall route had
                been pretty enough the day before, but it's one of
                the lower passes — busy with trucks and patches of
                roadworks — and the Nufenen had been the quieter,
                cleaner ride. Two of us voted to head back up. Quick
                spin to the top, coffee and apple strudel at the
                col, the long views down into Italy laid out below.
                The other two had earned a slower day at the chalet.
            </p>
        ),
        photos: [
            {
                src: "/assets/grand_tour_2026/d7_nufenen.jpg",
                alt: "Panoramic view from the top of the Nufenenpass — green Alpine slopes dropping into a deep valley with the pass road visible in the lower right, a long horizon of snow-capped peaks across the middle distance, bright blue sky with scattered clouds and a patch of snow in the foreground",
                caption: "The view from the top of Nufenenpass",
            },
        ],
    },
    {
        flag: "🇩🇪",
        place: "Laufenburg",
        title: "Loaded onto the Lötschberg",
        summary: "The car-transporter train, then north to the Rhine.",
        accent: "slate",
        narrative: (
            <>
                <p>
                    Heading north, we did the thing every rider should do
                    at least once: loaded the bikes onto the{" "}
                    <strong>car-transporter train</strong> at Goppenstein
                    and let the Lötschberg tunnel do the climbing for us.
                    You ride on, kill the engine, and the mountain you'd
                    otherwise spend an hour crossing slides by in the dark.
                </p>
                <p>
                    Out the other side we ran through{" "}
                    <strong>Interlaken</strong> and over toward the German
                    border, finishing the day at{" "}
                    <strong>Laufenburg</strong> — a small town that
                    straddles the Rhine right on the German-Swiss
                    border. Dinner was a proper hearty schnitzel and a
                    mountain of chips. Earned. Home felt closer now,
                    but there was still good riding between us and the
                    Channel.
                </p>
            </>
        ),
        photos: [
            {
                src: "/assets/grand_tour_2026/d8_train.jpg",
                alt: "Motorbikes lined up inside the Lötschberg car-transporter train carriage — a blue, white and red BMW GS in the foreground with its helmet resting on the seat, more bikes lined up behind, a rider standing near the front of the carriage and metal anchor points in the floor",
                caption: "Bikes loaded on the Lötschberg car train",
            },
            {
                src: "/assets/grand_tour_2026/d8_interlaken.jpg",
                alt: "A row of motorbikes parked at the kerb in front of the Interlaken welcome sign and Swiss flags, a leafless tree in the middle of the green, and the forested Bernese peaks rising behind under a bright blue sky",
                caption: "Arrival in Interlaken",
            },
            {
                src: "/assets/grand_tour_2026/d8_laufenburg.jpg",
                alt: "The old town of Laufenburg seen from across the Rhine — tightly packed cream, terracotta and yellow houses clustered along a steep riverbank above the curving green river, green hills rolling away in the distance under a moody grey sky",
                caption: "Laufenburg on the Rhine",
            },
            {
                src: "/assets/grand_tour_2026/d8_food.jpg",
                alt: "A hearty plate of breaded schnitzel topped with a creamy sauce and parsley, a generous mountain of golden chips alongside, garnished with an orange slice and curly lettuce, a glass of Swiss beer in the background",
                caption: "Hearty dinner: schnitzel and chips at Laufenburg",
            },
        ],
        hotel: {
            name: "Alte Post — Hotel Am Rhein-Ufer, Laufenburg",
            bookingUrl: "https://www.booking.com/hotel/de/alte-post-laufenburg.html",
            bookingSid: "grand_tour_2026_day8",
            tripadvisorUrl:
                "https://www.tripadvisor.com/Hotel_Review-g1826001-d2200633-Reviews-Hotel_Restaurant_Alte_Post-Laufenburg_Baden_Wurttemberg.html",
            body: (
                <>
                    A small three-star hotel on the German side of the
                    river — one we'd happily go back to. Friendly staff,
                    secure parking tucked down at the bottom of the hill
                    below the old town, an adjoining restaurant that
                    turned out the schnitzel that earned its place in this
                    report, and rooms looking across to the Swiss old town
                    opposite.
                </>
            ),
        },
    },
    {
        flag: "🇩🇪",
        place: "Baden-Baden",
        title: "Cuckoo clocks and the Black Forest",
        summary: "Less altitude than the Alps, but all the corners.",
        accent: "slate",
        narrative: (
            <p>
                The Black Forest doesn't have the altitude of the Alps,
                but it has the corners. We threaded the{" "}
                <strong>Schwarzwaldhochstraße</strong> — the high road
                through the pines — and stopped in{" "}
                <strong>Triberg</strong> at the famous House of Clocks,
                home to some of the largest cuckoo clocks in the world.
                It's gloriously, unashamedly kitsch, and exactly the
                sort of thing you should stop for on a long ride home.
                We ended the day in the spa town of{" "}
                <strong>Baden-Baden</strong>.
            </p>
        ),
        photos: [
            {
                src: "/assets/grand_tour_2026/d9_house_of_clocks.jpg",
                alt: "The 'Haus der 1000 Uhren / House of 1000 Clocks' in Triberg — a Black Forest chalet-style building with a giant green clock face mounted on the front and a tall pitched roof, a colourful umbrella over a picnic area in front, dark pine forest rising behind under a bright blue sky",
                caption: "House of 1000 Clocks, Triberg",
            },
        ],
        hotel: {
            name: "Hotel Rebstock, Neuweier (near Baden-Baden)",
            bookingUrl: "https://www.booking.com/hotel/de/rebstock-baden-baden.html",
            bookingSid: "grand_tour_2026_day9",
            tripadvisorUrl:
                "https://www.tripadvisor.com/Hotel_Review-g3513972-d8601359-Reviews-Hotel_Rebstock-Neuweier_Baden_Baden_Baden_Wurttemberg.html",
            body: (
                <>
                    A family-run four-star tucked in the vineyards above
                    the spa town — air-conditioned rooms (very welcome in
                    the summer heat), a well-stocked bar with a proper
                    selection of beers, and a menu that delivers.
                </>
            ),
        },
    },
    {
        flag: "🇧🇪",
        place: "Vresse-sur-Semois",
        title: "Into the Ardennes",
        summary: "Green, rolling, forested country with a castle round every other bend.",
        accent: "plum",
        narrative: (
            <p>
                A long day west brought us into Belgium and the{" "}
                <strong>Ardennes</strong> — green, rolling, forested
                country with a castle around every other bend, and
                roads good enough to be worth the slog. The catch: a
                lot of hydration stops in the heat and a couple of
                detours that turned a long day into a properly
                exhausting one. We stopped above{" "}
                <strong>Bouillon</strong> for the viewpoint over the
                medieval fortress crouched on its hairpin in the
                river, then pushed on, glad to roll into{" "}
                <strong>Vresse-sur-Semois</strong> for the night.
                Dinner was at <strong>Au Roy de la Bière</strong>.
                Earned. The Alps were behind us, but the riding
                stayed honest right to the end.
            </p>
        ),
        photos: [
            {
                src: "/assets/grand_tour_2026/d10_pitstop.jpg",
                alt: "Four motorbikes lined up single-file at a quiet roadside pitstop on the long ride west — riders milling around, a small rest area with a green bin, country road and rolling green forested hills under a clear blue sky",
                caption: "Roadside pitstop on the long ride west",
            },
        ],
    },
    {
        flag: "🇧🇪",
        place: "Ghent",
        title: "Ghent",
        summary: "The Belgian city that quietly out-charms its famous neighbours.",
        accent: "plum",
        narrative: (
            <>
                <p>
                    A shorter hop north to <strong>Ghent</strong>, one of
                    those Belgian cities that quietly out-charms its more
                    famous neighbours — canals, guild houses, and the kind
                    of beer that makes you grateful you're not riding any
                    further that day.
                </p>
                <p>
                    The hotel had great secure private parking for €19
                    — all four bikes tucked into one underground bay,
                    covers on. Plenty of other riders had taken the
                    free option, parked up on the street out front.
                    (A pleasant trip-wide discovery: most hotels along
                    the way charged us a single parking spot for all
                    four bikes squeezed together. Worth asking when you
                    book.)
                </p>
            </>
        ),
        photos: [
            {
                src: "/assets/grand_tour_2026/d11_parking.jpg",
                alt: "Four motorbikes parked together in a single bay (number 108) of an underground car park in Ghent, all covered with silver Oxford Aquatex motorbike covers, ordinary cars parked in the bays either side",
                caption: "Four bikes, one bay — Ghent",
            },
        ],
        hotel: {
            name: "Adagio Access Gent Centrum Dampoort, Ghent",
            bookingUrl:
                "https://www.booking.com/hotel/be/adagio-access-gent-centrum-dampoort.html",
            bookingSid: "grand_tour_2026_day11",
            tripadvisorUrl:
                "https://www.tripadvisor.com/Hotel_Review-g188666-d25409898-Reviews-Adagio_Access_Gent_Centrum_Dampoort-Ghent_East_Flanders_Province.html",
            body: (
                <>
                    A modern aparthotel a short walk from the centre —
                    studios with their own kitchen, the secure underground
                    parking pictured above, and a bar that serves properly
                    cold beer at the end of a long day.
                </>
            ),
        },
    },
    {
        flag: "🇬🇧",
        place: "Weybridge",
        title: "Home",
        summary: "One last run to the coast, back into the metal box.",
        accent: "teal",
        narrative: (
            <p>
                One last fuel stop, one last run to the coast, and back
                into the metal box at Coquelles for the trip under the
                Channel. We surfaced in England, peeled off one by one
                toward home in Weybridge, and that was that.
            </p>
        ),
    },
];

const NUMBERS = [
    { value: "12", label: "days on the road" },
    { value: "4", label: "riders, 4 bikes" },
    { value: "5", label: "countries crossed" },
    { value: "~2,000", label: "miles ridden" },
    { value: "5", label: "major passes — Furka, Susten, Grimsel, Nufenen, Simplon" },
    { value: "1", label: "car train through a mountain" },
    { value: "1", label: "scratched fairing, 1 snapped clutch lever — nobody hurt" },
    { value: "∞", label: "espressos, apple strudels and roadside jokes" },
];

function GrandTourAlps2026() {
    usePageMeta({
        title:
            "Four bikes, one tunnel, and the greatest roads in the Alps — Planamoto",
        description: META.summary,
        url: "https://www.planamoto.com/guides/grand-tour-europe",
    });

    return (
        <GuideArticle
            eyebrow="Ride report"
            title="Four Bikes, One Tunnel, and the Greatest Roads in the Alps"
            lede="Aliyah, Naveeda, Toby and me — four motorcycles pointed at the heart of the Alps with a simple mission: catch the great passes, eat well, come home with stories. Here's how it went."
            heroImage={{
                src: META.image!.src,
                alt: META.image!.alt ?? "A GS at a high Alpine pass, snowcaps behind",
            }}
            beforeSections={
                <>
                    <GuideTripLead
                        authorNote="Spencer · rode it fully-loaded on the GS"
                        heading="The trip at a glance"
                        intro="Twelve days, four bikes, five countries and some of the finest roads on the continent — ridden in good company and bloody hot weather. The shape of it, before the day-by-day."
                        stats={TRIP_STATS}
                        flags={ROUTE_FLAGS}
                        beforeYouGo={
                            <>
                                <b>Before you go:</b> the planning groundwork —{" "}
                                <Link to="/guides/packing-europe">what to pack</Link>,{" "}
                                <Link to="/guides/french-toll-roads">
                                    French toll roads
                                </Link>
                                ,{" "}
                                <Link to="/guides/fuel-prices">
                                    fuel prices by country
                                </Link>{" "}
                                and{" "}
                                <Link to="/guides/bike-security">
                                    bike security on tour
                                </Link>{" "}
                                — covers the boring-but-essential bits this report skips.
                            </>
                        }
                    />
                    <div className="gt-guide-transition">
                        <div className="gt-eyebrow gt-guide-transition__eyebrow">
                            Day by day
                        </div>
                        <h2 className="gt-guide-transition__heading">
                            Twelve days. Here's how it went.
                        </h2>
                        <p className="gt-guide-transition__intro">
                            From the Channel tunnel to the holy trinity of passes and
                            the long road home — one rail per day, the way the guides
                            read.
                        </p>
                    </div>
                </>
            }
            articleSchema={{
                slug: META.slug,
                datePublished: META.datePublished,
                dateModified: META.dateModified,
                image: META.image?.src,
                description: META.summary,
            }}
        >
            {DAYS.map((d, i) => (
                <GuideSection
                    key={d.title}
                    index={i}
                    day={{
                        catnum: String(i + 1).padStart(2, "0"),
                        flag: d.flag,
                        place: d.place,
                        title: d.title,
                        summary: d.summary,
                        accent: DAY_ACCENT[d.accent],
                    }}
                >
                    {d.narrative}
                    {d.photos && (
                        <GuideDayCarousel
                            variant="grid"
                            label={`Day ${i + 1} photos`}
                            items={d.photos}
                        />
                    )}
                    {d.hotel && (
                        <aside className="gt-tour-hotel">
                            <div className="gt-tour-hotel__eyebrow">Where we stayed</div>
                            <h4 className="gt-tour-hotel__name">{d.hotel.name}</h4>
                            <p className="gt-tour-hotel__body">{d.hotel.body}</p>
                            <div className="gt-tour-hotel__links">
                                <a
                                    href={wrapBookingWithCj(
                                        d.hotel.bookingUrl,
                                        d.hotel.bookingSid,
                                    )}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    Book on Booking.com
                                </a>
                                <a
                                    href={d.hotel.tripadvisorUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    Reviews on Tripadvisor
                                </a>
                            </div>
                        </aside>
                    )}
                </GuideSection>
            ))}

            <GuideSection
                index={DAYS.length}
                heading="The numbers"
                intro="Twelve days. Four bikes. Five countries. Some of the finest roads on the continent, ridden in good company and bloody hot weather."
                highlight={{ glyph: "★", background: "var(--gt-accent)", color: "#3a2a0a" }}
                stats={NUMBERS}
            />

            <GuideCtaBand
                eyebrow="Ride this trip yourself"
                heading="The full 12-day route is a clonable tour template."
                body="Clone it into your own tours, set your dates, invite your mates, and tweak any leg — stops, overnight addresses, expenses and the group all come across."
                primary={{
                    label: "Open the Grand Tour template",
                    to: "/explore/grand-tour-2026",
                }}
                secondary={{ label: "Browse tours", to: "/explore" }}
            />

            <RelatedGuides
                slugs={["packing-europe", "french-toll-roads", "bike-security"]}
            />
        </GuideArticle>
    );
}

export default GrandTourAlps2026;
